// InverterBrandsTable.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { fetchData } from "../helper/api.interceptor";
import EditInverterModal from "./EditInverterModal";
import DeleteInverterButton from "./DeleteInverterButton";

const InverterBrandsTable = () => {
    const [inverters, setInverters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [showModal, setShowModal] = useState(false);
    const [editData, setEditData] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);


    const totalPages = Math.ceil(inverters.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedData = inverters.slice(startIndex, startIndex + itemsPerPage);

    const fetchInverters = async () => {
        setLoading(true);
        try {
            const response = await fetchData.get("/inverter/inverters");
            if (Array.isArray(response?.result)) {
                setInverters(response.result);
            } else {
                setError("No inverters found");
            }
        } catch (err) {
            console.error("Error:", err);
            setError("Failed to load inverter data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInverters();
    }, []);

    const handleNext = () => {
        if (currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    const handlePrev = () => {
        if (currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    const handleItemsPerPageChange = (e) => {
        const value = parseInt(e.target.value, 10);
        setItemsPerPage(value);
        setCurrentPage(1); // reset to first page when changing per-page
    };

    const handleAddClick = () => {
        setEditData(null);
        setShowModal(true);
    };

    const handleEditClick = (inverter) => {
        setEditData(inverter);
        setShowModal(true);
    };

    if (loading)
        return <div className="text-center p-4">Loading inverters...</div>;
    if (error) return <div className="text-danger text-center p-4">{error}</div>;

    return (
        <>
            <div className="mt-4">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
                    <h6 className="fw-semibold mb-0">  <Icon icon="mdi:current-dc" className="menu-icon" style={{ width: "30px", height: "30px" }} /> | Inverter Brands</h6>
                    <ul className="d-flex align-items-center gap-2">
                        <li className="fw-medium">
                            <Link to="/" className="d-flex align-items-center gap-1 hover-text-primary">
                                <Icon icon="solar:home-smile-angle-outline" className="icon text-lg" />
                                Dashboard
                            </Link>
                        </li>
                        <li> - </li>
                        <li className="fw-medium"> All Inverter Brands</li>
                    </ul>
                </div>

                <div className="card">
                    <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-2">
                            <span>Show</span>
                            <select
                                className="form-select form-select-sm w-auto no-bg-position"
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                            >
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="15">15</option>
                                <option value="20">20</option>
                                <option value={inverters.length}>All</option>
                            </select>
                        </div>
                        <button className="btn btn-warning rounded-pill fw-medium d-flex align-items-center gap-2" onClick={handleAddClick}>
                            <Icon icon="mdi:plus" className="fs-5" />
                            Add Inverter
                        </button>
                    </div>

                    <div className="card-body table-responsive">
                        <table className="table bordered-table table-hover align-middle">
                            <thead>
                                <tr style={{ fontSize: "15px" }}>
                                    <th className="text-center">#</th>
                                    <th>Image</th>
                                    <th>Name</th>
                                    <th>Capacity</th>
                                    <th>Type</th>
                                    <th>Price</th>
                                    <th>Description</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedData.map((inv, index) => (
                                    <tr key={inv.id} style={{ fontSize: "15px" }}>
                                        <td className="text-center">
                                            {startIndex + index + 1}
                                        </td>
                                        <td>
                                            <img
                                                src={inv.image || "/placeholder.png"}
                                                style={{ width: "100px", height: "100px", objectFit: "contain" }}
                                                alt={inv.name}
                                            />
                                        </td>
                                        <td className="align-middle">{inv.name}</td>
                                        <td className="align-middle">{inv.capacity}</td>
                                        <td className="align-middle">{inv.type}</td>
                                        <td className="align-middle">{inv.price}</td>
                                        <td className="align-middle">{inv.description || "-"}</td>
                                        <td className="text-center">
                                            <div className="d-flex justify-content-center gap-2">
                                                <button
                                                    className="w-32-px h-32-px bg-warning-subtle text-warning rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                                    onClick={() => handleEditClick(inv)}
                                                >
                                                    <Icon icon="mdi:pencil-outline" className="fs-5" />
                                                </button>
                                                <DeleteInverterButton id={inv.id} onDeleted={fetchInverters} />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="d-flex justify-content-end align-items-center gap-4">
                            <button
                                className="btn btn-sm"
                                style={{
                                    backgroundColor: "#ff9800", // custom orange
                                    color: "white",
                                    border: "none",
                                    padding: "10px 40px",
                                    height: "auto"
                                }}
                                onClick={handlePrev}
                                disabled={currentPage === 1}
                            >
                                Prev
                            </button>

                            <span>
                                Page {currentPage} of {totalPages}
                            </span>

                            <button
                                className="btn btn-sm"
                                onClick={handleNext}
                                style={{
                                    backgroundColor: "#ff9800", // custom orange
                                    color: "white",
                                    border: "none",
                                    padding: "10px 40px",
                                    height: "auto"
                                }}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                {showModal && (
                    <EditInverterModal
                        show={showModal}
                        onClose={() => setShowModal(false)}
                        editData={editData}
                        refreshList={fetchInverters}
                    />
                )}
            </div>
        </>
    );
};

export default InverterBrandsTable;